import {useRouter} from 'next/router';
import {ParsedUrlQuery} from 'querystring';
import React, {useEffect, useState} from 'react';

import {
    trackAmplitudeEvent,
    BannerClickEvent as AmplitudeBannerClickEvent,
    NoticeType,
} from '@udemy/amplitude';
import {BrowsePageContent, CertificationTopic, PaginatedContent} from '@udemy/browse-aggregator';
import {Tracker} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {Image} from '@udemy/react-core-components';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {Banner, BannerButtonStack, BannerTitle} from '@udemy/react-messaging-components';

import {INDEED_LINKS_ON_AGGREGATOR_PAGES} from 'src/lib/experimentation-constants';

import {CertificationAggregatorCourseDirectory} from '../../components/aggregator-course-directory/certification-aggregator-course-directory.react-component';
import styles from './certification-aggregator.module.less';
import {AggregatorHeroWrapper} from './components/aggregator-hero-wrapper/aggregator-hero-wrapper';
import {RelatedCertificationAggregatorCarouselWrapper} from './components/certification-aggregator-carousel-wrapper/certification-aggregator-carousel-wrapper.react-component';
import {CertificationTopicCarouselWrapper} from './components/certification-topic-carousel-wrapper/certification-topic-carousel-wrapper.react-component';
import {NOTICE_TYPE, PAGE_KEY} from './constants';
import {indeedLinks} from './data/indeed-links';
import {BannerClickEvent, BannerImpressionEvent} from './events/events';

export const CARD_CAROUSEL_PAGE_SIZE = 24;
export const MAX_CARDS_ON_STACKED_VIEW = 16;

export type CertificationAggregatorProps = {
    params: ParsedUrlQuery;
    relatedAggregatorsComponentInitialData?: PaginatedContent<BrowsePageContent>;
    certificationTopicCarouselComponentInitialData: PaginatedContent<CertificationTopic>;
};

export function CertificationAggregator(props: CertificationAggregatorProps) {
    const {gettext} = useI18n();
    const isMobile = useMatchMedia('mobile-max');
    const [isBannerVisible, setIsBannerVisible] = useState(true);
    const router = useRouter();
    const aggregator = router.query?.aggregator ?? null;
    const indeedLink = aggregator ? indeedLinks[aggregator[0] as keyof typeof indeedLinks] : null;
    const {featureVariantAssignments, isLoading} = useFeatures([INDEED_LINKS_ON_AGGREGATOR_PAGES]);
    const indeedLinksEnabled = isLoading
        ? false
        : featureVariantAssignments.get(INDEED_LINKS_ON_AGGREGATOR_PAGES)?.configuration
              .indeedLinksEnabled;
    false;

    const handleBannerClick = () => {
        Tracker.publishEvent(
            new BannerClickEvent({
                noticeType: NOTICE_TYPE.PARTNERSHIP,
                pageKey: PAGE_KEY.BROWSE_CERTIFICATION_ISSUER_AGGREGATOR,
                url: indeedLink,
                noticeId: 0,
                slideNumber: null,
                uiRegion: null,
            }),
        );
        trackAmplitudeEvent(
            new AmplitudeBannerClickEvent(
                0,
                NOTICE_TYPE.PARTNERSHIP as unknown as NoticeType,
                undefined,
                undefined,
                undefined,
                undefined,
                indeedLink as string,
                undefined,
            ),
        );
    };

    useEffect(() => {
        if (indeedLinksEnabled)
            Tracker.publishEvent(
                new BannerImpressionEvent({
                    noticeId: 0,
                    noticeType: NOTICE_TYPE.PARTNERSHIP,
                    pageKey: PAGE_KEY.BROWSE_CERTIFICATION_ISSUER_AGGREGATOR,
                    slideNumber: null,
                    url: null,
                    uiRegion: null,
                }),
            );
    }, [indeedLinksEnabled]);

    return (
        <div data-testid={'certification-aggregator'}>
            <section className={styles['fill-background']}>
                <div className={styles['aggregator-hero-banner']}>
                    <AggregatorHeroWrapper />
                </div>
            </section>
            <section className={styles['ud-container-with-common-padding--only-top']}>
                <CertificationTopicCarouselWrapper
                    initialData={props.certificationTopicCarouselComponentInitialData}
                />
            </section>
            {props.relatedAggregatorsComponentInitialData && (
                <section className={styles['ud-container-with-common-padding--only-top']}>
                    <RelatedCertificationAggregatorCarouselWrapper
                        initialData={props.relatedAggregatorsComponentInitialData}
                    />
                </section>
            )}

            {indeedLinksEnabled && indeedLink && isBannerVisible && (
                <section className={styles['ud-container-with-common-padding--only-top']}>
                    <Banner
                        ctaButtons={
                            <BannerButtonStack
                                primaryOnClick={handleBannerClick}
                                primaryButtonText={gettext('See jobs')}
                                primaryButtonProps={{
                                    href: indeedLink,
                                    componentClass: 'a',
                                    target: isMobile ? undefined : '_blank',
                                    rel: isMobile ? undefined : 'noopener noreferrer',
                                }}
                            />
                        }
                        onDismissClick={() => setIsBannerVisible(false)}
                        show={isBannerVisible}
                        theme="accent-dark"
                        title={
                            <BannerTitle>{gettext('Help wanted. Skills required.')}</BannerTitle>
                        }
                        leftImageArea={
                            <Image
                                src="https://frontends.udemycdn.com/frontends-browse-aggregator/staticx/udemy/images/logos/carreer_aggregator_indeed_logo.png"
                                alt="indeed logo"
                                height={120}
                                width={120}
                            />
                        }
                    >
                        {gettext(
                            "We've teamed up with Indeed to help turn your Udemy skills into your dream job. Explore open roles today.",
                        )}
                    </Banner>
                </section>
            )}

            <section className={styles['ud-container-with-common-padding--only-top']}>
                <CertificationAggregatorCourseDirectory />
            </section>
        </div>
    );
}

export {INDEED_LINKS_ON_AGGREGATOR_PAGES};
